import { createTheme } from '@mui/material/styles';

// ZycoTM dashboard theme — deliberately matches the desktop app's palette
// (D:\ZycoTM_New\src\style.css): dark indigo/violet gradient background,
// glass-effect surfaces, same indigo primary + status colors, so the web
// dashboard reads as the same product rather than a different app.
const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#4F46E5',
            light: '#818CF8',
            dark: '#4338CA',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#7C3AED',
            light: '#A78BFA',
            dark: '#5B21B6',
        },
        error: {
            main: '#EF4444',
            light: '#FCA5A5',
            dark: '#DC2626',
        },
        warning: {
            main: '#F59E0B',
            light: '#FCD34D',
            dark: '#D97706',
        },
        success: {
            main: '#10B981',
            light: '#6EE7B7',
            dark: '#059669',
        },
        background: {
            // Solid fallback behind the gradient painted on <body> (see
            // MuiCssBaseline below) — same stop the desktop app's gradient ends on.
            default: '#1e1b4b',
            paper: 'rgba(255,255,255,0.05)',
        },
        text: {
            primary: '#FFFFFF',
            secondary: '#9CA3AF', // --gray-400
            disabled: '#6B7280',  // --gray-500
        },
        divider: 'rgba(255,255,255,0.08)',
    },
    typography: {
        fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
        h1: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em' },
        h2: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' },
        h3: { fontWeight: 600, fontSize: '1.25rem' },
        h4: { fontWeight: 600, fontSize: '1.125rem' },
        h5: { fontWeight: 600, fontSize: '1rem' },
        h6: { fontWeight: 600, fontSize: '0.875rem' },
        subtitle1: { fontWeight: 500, fontSize: '0.938rem', color: '#9CA3AF' },
        subtitle2: { fontWeight: 500, fontSize: '0.813rem', color: '#9CA3AF' },
        body1: { fontSize: '0.938rem', lineHeight: 1.6 },
        body2: { fontSize: '0.813rem', lineHeight: 1.5 },
        button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0 1px 2px rgba(0,0,0,0.2)',
        '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
        '0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -1px rgba(0,0,0,0.2)',
        '0 10px 15px -3px rgba(0,0,0,0.35), 0 4px 6px -2px rgba(0,0,0,0.2)',
        '0 20px 25px -5px rgba(0,0,0,0.4), 0 10px 10px -5px rgba(0,0,0,0.2)',
        ...Array(19).fill('0 25px 50px -12px rgba(0,0,0,0.5)'),
    ],
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    // Same gradient + fixed attachment as the desktop app's body
                    // background, so scrolling content doesn't slide the gradient.
                    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
                    backgroundAttachment: 'fixed',
                    minHeight: '100vh',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    padding: '10px 24px',
                    boxShadow: 'none',
                    '&:hover': { boxShadow: '0 2px 8px rgba(79,70,229,0.4)' },
                },
                contained: {
                    '&:hover': { transform: 'translateY(-1px)' },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    backgroundImage: 'none',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(12px)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    backgroundImage: 'none',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(12px)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 10,
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&.Mui-focused fieldset': { borderColor: '#4F46E5' },
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    backgroundImage: 'none',
                    backdropFilter: 'blur(12px)',
                    boxShadow: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: 'rgba(30,27,75,0.55)',
                    backgroundImage: 'none',
                    backdropFilter: 'blur(12px)',
                    borderRight: '1px solid rgba(255,255,255,0.08)',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#2a2660',
                    backgroundImage: 'none',
                    border: '1px solid rgba(255,255,255,0.08)',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: 'rgba(255,255,255,0.08)' },
            },
        },
    },
});

export default theme;
